import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import "./Blog.css";

function BestShopifyApps() {
  const apps = [
    {
      name: "Wave Notify",
      category: "Inventory & Sales Recovery",
      description: "Capture lost sales from out-of-stock products with automated email notifications. Perfect for merchants who want to recover revenue when inventory returns.",
      link: "/wavenotify",
      features: ["Auto notifications", "Customer segments", "Analytics dashboard"],
    },
    {
      name: "Wave Sync",
      category: "Product Management",
      description: "Keep products in sync across multiple channels. Sync inventory, prices, and status between paired products with automatic updates.",
      link: "/wavesync",
      features: ["Bidirectional sync", "Real-time updates", "Analytics"],
    },
  ];

  const categories = [
    {
      title: "Inventory Management Apps",
      description: "Essential apps for keeping track of stock levels, syncing inventory across channels, and preventing overselling.",
      apps: ["Wave Sync", "Wave Notify"],
    },
    {
      title: "Sales Recovery Apps",
      description: "Apps that help you capture sales from out-of-stock products and abandoned carts.",
      apps: ["Wave Notify"],
    },
    {
      title: "Product Management Apps",
      description: "Tools for managing product data, syncing information, and keeping everything organized.",
      apps: ["Wave Sync"],
    },
  ];

  return (
    <>
      <SEO />
      <motion.div
        className="blog-wrapper"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <article className="blog-article">
          <header className="blog-header">
            <h1>Best Shopify Apps for Ecommerce Success in 2025</h1>
            <p className="blog-meta">
              Published: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </header>

          <div className="blog-content">
            <section className="blog-section">
              <h2>Introduction</h2>
              <p>
                Running a successful Shopify store requires more than just great products. You need the right tools to manage inventory, 
                recover lost sales, and keep everything running smoothly. In this guide, we'll explore the best Shopify apps that can 
                help take your ecommerce business to the next level.
              </p>
            </section>

            {categories.map((category, idx) => (
              <section key={idx} className="blog-section">
                <h2>{category.title}</h2>
                <p>{category.description}</p>
                
                {apps
                  .filter(app => category.apps.includes(app.name))
                  .map((app, appIdx) => (
                    <div key={appIdx} className="app-feature-box">
                      <h3>
                        <Link to={app.link} className="app-link">
                          {app.name}
                        </Link>
                      </h3>
                      <p className="app-category">{app.category}</p>
                      <p>{app.description}</p>
                      <ul className="app-features">
                        {app.features.map((feature, fIdx) => (
                          <li key={fIdx}>{feature}</li>
                        ))}
                      </ul>
                      <Link to={app.link} className="cta-button">
                        Learn More →
                      </Link>
                    </div>
                  ))}
              </section>
            ))}

            <section className="blog-section">
              <h2>Why These Apps Matter</h2>
              <p>
                The right Shopify apps can make the difference between a struggling store and a thriving business. 
                Apps like Wave Notify and Wave Sync solve real problems:
              </p>
              <ul>
                <li><strong>Recover Lost Sales:</strong> Wave Notify helps you capture revenue from out-of-stock products by automatically notifying customers when inventory returns.</li>
                <li><strong>Keep Inventory Accurate:</strong> Wave Sync ensures your products stay in sync across channels, preventing overselling and stock discrepancies.</li>
                <li><strong>Save Time:</strong> Automation means less manual work, giving you more time to focus on growing your business.</li>
              </ul>
            </section>

            <section className="blog-section">
              <h2>Getting Started</h2>
              <p>
                Ready to improve your Shopify store? Start by installing apps that address your biggest pain points. 
                Both <Link to="/wavenotify">Wave Notify</Link> and <Link to="/wavesync">Wave Sync</Link> offer free plans, 
                so you can try them risk-free and see the impact on your business.
              </p>
              <p>
                Visit the <a href="https://apps.shopify.com" target="_blank" rel="noopener noreferrer">Shopify App Store</a> to 
                explore these apps and find the perfect tools for your store.
              </p>
            </section>

            <section className="blog-section">
              <h2>Conclusion</h2>
              <p>
                The best Shopify apps are the ones that solve real problems and save you time. Whether you need to recover 
                lost sales, sync inventory, or automate workflows, there's an app that can help. Start with free plans, 
                measure the impact, and scale up as your business grows.
              </p>
            </section>
          </div>
        </article>
      </motion.div>
    </>
  );
}

export default BestShopifyApps;

